<script setup lang="ts">
// `forceExpanded` lets callers (e.g. the hamburger-menu popover) render this
// outside the sidebar with full labels regardless of sidebar collapsed state.
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSidebarStore } from '@/stores/sidebar'
import { useNavRefresh } from '@/composables/useNavRefresh'

const props = defineProps<{ forceExpanded?: boolean }>()
const emit = defineEmits<{ (e: 'navigate'): void }>()

const auth = useAuthStore()
const sidebar = useSidebarStore()
const route = useRoute()
const navRefresh = useNavRefresh()

const isCollapsed = computed(() => (props.forceExpanded ? false : sidebar.isCollapsed))

function handleNavClick(path: string) {
  if (route.path === path) {
    navRefresh.refresh()
  }
  emit('navigate')
}
</script>

<template>
  <div class="sidebar-nav-group" :class="{ collapsed: isCollapsed }">
    <!-- Primary navigation. Always visible; labels hide when collapsed. -->
    <nav class="nav" aria-label="Primary">
      <RouterLink
        v-tooltip.right="{ value: isCollapsed ? 'Projects' : undefined, showDelay: 300 }"
        class="nav-item"
        to="/projects"
        @click="handleNavClick('/projects')"
      >
        <span class="nav-item__icon-slot">
          <i class="pi pi-folder" aria-hidden="true" />
        </span>
        <span class="nav-label">Projects</span>
      </RouterLink>
      <RouterLink
        v-if="auth.isAdmin"
        v-tooltip.right="{ value: isCollapsed ? 'Users' : undefined, showDelay: 300 }"
        class="nav-item"
        to="/admin/users"
        data-testid="nav-admin-users"
        @click="handleNavClick('/admin/users')"
      >
        <i class="pi pi-users" aria-hidden="true" />
        <span class="nav-label">Users</span>
      </RouterLink>
      <RouterLink
        v-if="auth.isAdmin"
        v-tooltip.right="{ value: isCollapsed ? 'Security' : undefined, showDelay: 300 }"
        class="nav-item"
        to="/admin/security"
        data-testid="nav-admin-security"
        @click="handleNavClick('/admin/security')"
      >
        <i class="pi pi-shield" aria-hidden="true" />
        <span class="nav-label">Security</span>
      </RouterLink>
      <RouterLink
        v-if="auth.isAdmin"
        v-tooltip.right="{ value: isCollapsed ? 'Integrations' : undefined, showDelay: 300 }"
        class="nav-item"
        to="/admin/integrations"
        data-testid="nav-admin-integrations"
        @click="handleNavClick('/admin/integrations')"
      >
        <i class="pi pi-key" aria-hidden="true" />
        <span class="nav-label">Integrations</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.sidebar-nav-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-0_5);
}

.nav-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--nav-item-padding-y) var(--space-3);
  color: var(--text-secondary);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  font: inherit;
  text-decoration: none;
  width: 100%;
}

.nav-item i {
  font-size: var(--font-size-nav);
  width: var(--icon-size-nav);
  text-align: center;
  flex-shrink: 0;
}

/* Anchor for the collapsed-mode corner-overlay dot. The icon-slot is
   relatively positioned and sized to the nav icon; the dot's absolute
   coordinates resolve against this slot, not the whole nav-item row,
   so the dot consistently lands on the icon's top-right corner in
   both collapsed and (no-op) expanded mode. */
.nav-item__icon-slot {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-item:hover:not([disabled]) {
  background: var(--bg-card-hover);
  color: var(--text-primary);
}

.nav-item:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-inset);
}

.nav-item.router-link-active {
  background: var(--green-faint);
  color: var(--green-primary);
}

html.moria-dark .nav-item.router-link-active {
  color: var(--green-light);
}

.nav-label {
  overflow: hidden;
  white-space: nowrap;
  max-width: var(--sidebar-content-max-w);
  opacity: 1;
  transition:
    max-width var(--duration-slow) var(--ease-default),
    opacity var(--duration-slow) var(--ease-default);
}

/* Collapsed mode: center-align each nav item over its icon, drop label gap. */
.sidebar-nav-group.collapsed .nav-item {
  justify-content: center;
  padding: var(--nav-item-padding-y) 0;
  gap: 0;
}

.sidebar-nav-group.collapsed .nav-label {
  max-width: 0;
  opacity: 0;
}
</style>
